import { useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

import { AppCard, AppPage } from '../../components/general';
import { DataTable, TableDefinition } from '../../components/datatable';
import { useVoxTestCases, useSupportedVoxVendors, useVoxTestCaseMutations } from '../../hooks/useVoxTestCases';
import { CreateOrUpdateVoxTestCaseViewModel, VoxTestCaseViewModel } from '../../models/voxTestCase';
import { VoxTestCaseDialog } from './VoxTestCaseDialog';

interface DialogState {
  open: boolean;
  testCase: VoxTestCaseViewModel | null;
}

const overrideCount = (testCase: VoxTestCaseViewModel): number => Object.keys(testCase.overrides ?? {}).length;

const VoxTestCases = () => {
  const toastRef = useRef<Toast>(null);
  const [vendorFilter, setVendorFilter] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [dialogState, setDialogState] = useState<DialogState>({ open: false, testCase: null });

  const { data: vendors = [] } = useSupportedVoxVendors();
  const { data: testCases = [], isLoading } = useVoxTestCases(vendorFilter ?? undefined);
  const { createTestCase, updateTestCase, deleteTestCase, isSaving } = useVoxTestCaseMutations();

  const showError = (detail: string) =>
    toastRef.current?.show({ severity: 'error', summary: 'Error', detail, life: 6000 });
  const showSuccess = (detail: string) =>
    toastRef.current?.show({ severity: 'success', summary: 'Success', detail, life: 3000 });

  const handleSave = async (payload: CreateOrUpdateVoxTestCaseViewModel) => {
    try {
      if (payload.voxTestCaseId) {
        await updateTestCase({ voxTestCaseId: payload.voxTestCaseId, payload });
        showSuccess('Test case updated');
      } else {
        await createTestCase(payload);
        showSuccess('Test case created');
      }
      setDialogState({ open: false, testCase: null });
    } catch (error: any) {
      showError(error?.response?.data?.errors || error?.response?.data || error?.message || 'Failed to save test case');
    }
  };

  const handleDelete = (testCase: VoxTestCaseViewModel) => {
    confirmDialog({
      header: 'Delete Test Case',
      message: `Delete test case "${testCase.name}" for ${testCase.vendor}?`,
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await deleteTestCase(testCase.voxTestCaseId);
          showSuccess('Test case deleted');
        } catch (error: any) {
          showError(error?.response?.data?.errors || error?.message || 'Failed to delete test case');
        }
      },
    });
  };

  const activeBodyTemplate = (testCase: VoxTestCaseViewModel) => (
    <Tag value={testCase.active ? 'Active' : 'Inactive'} severity={testCase.active ? 'success' : 'secondary'} />
  );

  const overridesBodyTemplate = (testCase: VoxTestCaseViewModel) => `${overrideCount(testCase)} override(s)`;

  const actionBodyTemplate = (testCase: VoxTestCaseViewModel) => (
    <div className="flex">
      <Button
        icon="pi pi-pencil"
        className="p-button-rounded p-button-text"
        onClick={() => setDialogState({ open: true, testCase })}
        tooltip="Edit"
        tooltipOptions={{ position: 'left' }}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-text"
        onClick={() => handleDelete(testCase)}
        tooltip="Delete"
        tooltipOptions={{ position: 'left' }}
        severity="danger"
      />
    </div>
  );

  const tableDef: TableDefinition<VoxTestCaseViewModel> = {
    columns: [
      { field: 'vendor', header: 'Vendor', dataType: 'text' },
      { field: 'name', header: 'Name', dataType: 'text' },
      { field: 'description', header: 'Description', dataType: 'text' },
      { header: 'Overrides', body: overridesBodyTemplate, dataType: 'none', sortable: false, filter: false },
      { header: 'Active', body: activeBodyTemplate, dataType: 'none', sortable: false, filter: false },
      { field: 'updatedDate', header: 'Updated Date', dataType: 'date' },
      { header: 'Actions', body: actionBodyTemplate, dataType: 'none', sortable: false, filter: false },
    ],
  };

  const header = (
    <div className="flex flex-col lg:flex-row justify-between gap-3 p-input-filled">
      <div className="flex flex-col sm:flex-row gap-3">
        <span className="p-input-icon-left">
          <i className="pi pi-search text-gray-400" />
          <InputText
            className="min-w-[280px] !rounded-lg !border-gray-200 !text-[0.85rem]"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
          />
        </span>
        <Dropdown
          value={vendorFilter}
          options={vendors.map((vendor) => ({ label: vendor, value: vendor }))}
          onChange={(e) => setVendorFilter(e.value ?? null)}
          placeholder="All vendors"
          showClear
          className="min-w-[200px]"
        />
      </div>
      <div>
        <Button
          icon="pi pi-plus"
          label="Add Test Case"
          className="p-button-sm !rounded-lg"
          onClick={() => setDialogState({ open: true, testCase: null })}
        />
      </div>
    </div>
  );

  return (
    <AppPage
      title="VoX Test Cases"
      breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'VoX Test Cases' }]}
    >
      <AppCard>
        <Toast ref={toastRef} />
        <ConfirmDialog />
        <DataTable
          loading={isLoading}
          header={header}
          tableDef={tableDef}
          data={testCases}
          globalFilterValue={search}
        />
      </AppCard>

      {dialogState.open && (
        <VoxTestCaseDialog
          key={dialogState.testCase?.voxTestCaseId ?? 'new'}
          visible={dialogState.open}
          onClose={() => setDialogState({ open: false, testCase: null })}
          onSave={handleSave}
          initialTestCase={dialogState.testCase}
          vendors={vendors}
          saving={isSaving}
        />
      )}
    </AppPage>
  );
};

export default VoxTestCases;
